import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Download, AlertCircle, RefreshCw, Filter, FileSpreadsheet,
    ChevronLeft, ChevronRight, Globe, Clock, User, Loader2, X, ShieldCheck
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { fetchExportAudit } from "../../services/crmServices/logService";

export function ExportAuditLog() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
    const [summary, setSummary] = useState({ totalExports: 0, totalRows: 0, failed: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [page, setPage] = useState(1);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [successFilter, setSuccessFilter] = useState(""); // "" | "true" | "false"
    const [expanded, setExpanded] = useState(null); // id of row showing details

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchExportAudit({
                page,
                limit: 50,
                from: from || undefined,
                to: to ? new Date(to + "T23:59:59").toISOString() : undefined,
                success: successFilter || undefined
            });
            setRows(res?.data?.rows || []);
            setPagination(res?.data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
            setSummary(res?.data?.summary || { totalExports: 0, totalRows: 0, failed: 0 });
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load audit log");
        } finally {
            setLoading(false);
        }
    }, [page, from, to, successFilter]);

    useEffect(() => { load(); }, [load]);

    // ── Admin gate ──────────────────────────────────────────────────
    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-12">
                <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                    <div className="p-3 bg-red-50 rounded-full w-fit mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-sm text-gray-500">Administrator privileges required.</p>
                </div>
            </div>
        );
    }

    const clearFilters = () => {
        setFrom("");
        setTo("");
        setSuccessFilter("");
        setPage(1);
    };

    const hasFilters = from || to || successFilter;

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-sm">
                        <ShieldCheck className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Export Audit Log</h1>
                        <p className="text-xs text-gray-500">Track who has downloaded lead exports from this CRM</p>
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                    {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Refresh
                </Button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                <StatCard
                    icon={Download}
                    label="Total exports"
                    value={summary.totalExports.toLocaleString()}
                    color="bg-indigo-50 text-indigo-700"
                />
                <StatCard
                    icon={FileSpreadsheet}
                    label="Total rows exported"
                    value={summary.totalRows.toLocaleString()}
                    color="bg-emerald-50 text-emerald-700"
                />
                <StatCard
                    icon={AlertCircle}
                    label="Failed attempts"
                    value={summary.failed.toLocaleString()}
                    color="bg-red-50 text-red-700"
                />
            </div>

            {/* Filters */}
            <Card className="mb-4">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">From</label>
                            <Input
                                type="date"
                                value={from}
                                onChange={(e) => { setFrom(e.target.value); setPage(1); }}
                                className="h-9 w-44"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">To</label>
                            <Input
                                type="date"
                                value={to}
                                onChange={(e) => { setTo(e.target.value); setPage(1); }}
                                className="h-9 w-44"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Status</label>
                            <select
                                value={successFilter}
                                onChange={(e) => { setSuccessFilter(e.target.value); setPage(1); }}
                                className="h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">All</option>
                                <option value="true">Successful</option>
                                <option value="false">Failed</option>
                            </select>
                        </div>
                        {hasFilters && (
                            <Button size="sm" variant="outline" onClick={clearFilters}>
                                <X className="h-3.5 w-3.5 mr-1" />
                                Clear filters
                            </Button>
                        )}
                        <div className="ml-auto text-xs text-gray-500">
                            <Filter className="inline h-3 w-3 mr-1" />
                            Showing {rows.length} of {pagination.total} records
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center">
                            <Loader2 className="h-6 w-6 animate-spin text-indigo-500 mx-auto mb-2" />
                            <p className="text-xs text-gray-500">Loading audit log...</p>
                        </div>
                    ) : error ? (
                        <div className="p-12 text-center">
                            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center">
                            <Download className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-700">No exports yet</p>
                            <p className="text-xs text-gray-500 mt-1">Lead exports will appear here as they happen.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">When</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">User</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Filters</th>
                                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Rows</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">IP</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.map((row) => {
                                        const ex = row.export || {};
                                        const isExpanded = expanded === row._id;
                                        const filtersText = formatFilters(ex);
                                        return (
                                            <>
                                                <tr
                                                    key={row._id}
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => setExpanded(isExpanded ? null : row._id)}
                                                >
                                                    <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                                                        <Clock className="inline h-3 w-3 mr-1 text-gray-400" />
                                                        {formatTime(row.createdAt)}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="h-6 w-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold">
                                                                {(ex.staffName || "?")[0]}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="text-xs font-medium text-gray-900 truncate">{ex.staffName || "Unknown"}</div>
                                                                {ex.staffEmail && <div className="text-[10px] text-gray-500 truncate">{ex.staffEmail}</div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-700">
                                                        {filtersText || <span className="text-gray-400 italic">All leads</span>}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-right text-xs font-bold tabular-nums text-gray-900">
                                                        {(ex.leadCount ?? 0).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">
                                                        <Globe className="inline h-3 w-3 mr-1 text-gray-400" />
                                                        {ex.ipAddress || "-"}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        {ex.success ? (
                                                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] h-5">Success</Badge>
                                                        ) : (
                                                            <Badge className="bg-red-100 text-red-700 text-[10px] h-5">Failed</Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-indigo-50/30">
                                                        <td colSpan="6" className="px-4 py-3">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
                                                                <DetailRow label="File name" value={ex.fileName} mono />
                                                                <DetailRow label="Duration" value={ex.durationMs ? `${ex.durationMs} ms` : "-"} />
                                                                <DetailRow label="User agent" value={ex.userAgent || "-"} className="md:col-span-2" mono small />
                                                                {ex.sources?.length > 0 && <DetailRow label="Sources" value={ex.sources.join(", ")} />}
                                                                {ex.countries?.length > 0 && <DetailRow label="Countries" value={ex.countries.join(", ")} />}
                                                                {ex.errorMessage && <DetailRow label="Error" value={ex.errorMessage} className="md:col-span-2 text-red-600" />}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                            <div className="text-xs text-gray-500">
                                Page {pagination.page} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page <= 1}
                                >
                                    <ChevronLeft className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                                    disabled={page >= pagination.totalPages}
                                >
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className={`rounded-lg p-4 ${color} flex items-center justify-between`}>
            <div>
                <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
                <div className="text-2xl font-bold tabular-nums">{value}</div>
            </div>
            <Icon className="h-7 w-7 opacity-50" />
        </div>
    );
}

function DetailRow({ label, value, mono, small, className = "" }) {
    return (
        <div className={`flex items-start gap-2 ${className}`}>
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide shrink-0 w-20">{label}:</span>
            <span className={`text-gray-700 break-all ${mono ? "font-mono" : ""} ${small ? "text-[10px]" : ""}`}>
                {value || "-"}
            </span>
        </div>
    );
}

function formatTime(iso) {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false
    });
}

function formatFilters(ex) {
    const parts = [];
    if (ex.sources?.length > 0) parts.push(`${ex.sources.length} source${ex.sources.length > 1 ? "s" : ""}`);
    if (ex.countries?.length > 0) parts.push(`${ex.countries.length} countr${ex.countries.length > 1 ? "ies" : "y"}`);
    return parts.join(" + ");
}
